/**
 * Cmarkdownlistitem Component
 * List item component for markdown rendering
 */
'use client';
import type { ReactNode, CSSProperties } from 'react';
import { IconCheck } from '@tabler/icons-react';
import { Ctext } from '../Ctext';
import { ColorType } from '@/theme/colors';
import { FontWeight } from '@/theme/typography';
import { getRadiusCssValue } from '@/theme/radius';
import { pxToRem } from '@/theme/spacing';
export interface CmarkdownlistitemProps {
    children: ReactNode;
    ordered?: boolean;
    index?: number;
    task?: boolean;
    checked?: boolean;
    className?: string;
    style?: CSSProperties;
    /** Hebrew-led item: render right-to-left (marker moves to the right). */
    hebrew?: boolean;
    /**
     * Ordered-marker override, printed verbatim in place of `${index}.`.
     * Paren lists (`a) b) c)`) use it to keep the author's own letters.
     */
    marker?: string;
}
const RTL_BLOCK_STYLE: CSSProperties = { direction: 'rtl' };
const CHECKBOX_SIZE_PX = 16;
const CHECKBOX_RADIUS_PX = 4;
const CHECKBOX_ICON_SIZE_PX = 12;
const CHECKBOX_ICON_STROKE = 3;
const CHECKBOX_FILL_UNCHECKED = 'rgba(102, 254, 193, 0.08)';
const CHECKBOX_FILL_CHECKED = 'rgba(102, 254, 193, 0.95)';
const CHECKBOX_GLYPH_COLOR = '#04130D';
const CHECKBOX_SHADOW_UNCHECKED = 'inset 0 0 0 1px rgba(102, 254, 193, 0.55), rgba(0, 0, 0, 0.55) 0 1px 3px 0';
const CHECKBOX_SHADOW_CHECKED = 'inset 1px 1px 0 0 rgba(255, 255, 255, 0.45), 0 0 8px 0 rgba(102, 254, 193, 0.35), rgba(0, 0, 0, 0.6) 0 1px 3px 0';
const VISUALLY_HIDDEN_INPUT_STYLE: CSSProperties = {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    border: 0,
    clip: 'rect(0, 0, 0, 0)',
};
const CHECKBOX_WRAPPER_STYLE: CSSProperties = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: pxToRem(CHECKBOX_SIZE_PX),
    height: pxToRem(CHECKBOX_SIZE_PX),
    marginTop: pxToRem(4),
    flexShrink: 0,
};
const CHECKBOX_VISUAL_BASE_STYLE: CSSProperties = {
    width: '100%',
    height: '100%',
    borderRadius: getRadiusCssValue(CHECKBOX_RADIUS_PX),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
};
export function Cmarkdownlistitem({ children, ordered = false, index = 1, task = false, checked = false, className, style, hebrew = false, marker, }: CmarkdownlistitemProps) {
    const wrapperStyle: CSSProperties = {
        display: 'flex',
        gap: pxToRem(8),
        alignItems: 'flex-start',
        width: '100%',
        minWidth: 0,
        ...(hebrew ? RTL_BLOCK_STYLE : null),
        ...style,
    };
    return (
      <div className={className} style={wrapperStyle}>
      {/* Marker: checkbox / number / bullet */}
      <div style={{
            display: 'flex',
            minWidth: pxToRem(20),
            paddingTop: pxToRem(2),
        }}>
        {task ? (<span style={CHECKBOX_WRAPPER_STYLE}>
            <input type="checkbox" disabled readOnly checked={checked} tabIndex={-1} style={VISUALLY_HIDDEN_INPUT_STYLE}/>
            <span aria-hidden="true" style={{
                ...CHECKBOX_VISUAL_BASE_STYLE,
                backgroundColor: checked ? CHECKBOX_FILL_CHECKED : CHECKBOX_FILL_UNCHECKED,
                boxShadow: checked ? CHECKBOX_SHADOW_CHECKED : CHECKBOX_SHADOW_UNCHECKED,
            }}>
              {checked && (<IconCheck size={CHECKBOX_ICON_SIZE_PX} stroke={CHECKBOX_ICON_STROKE} color={CHECKBOX_GLYPH_COLOR}/>)}
            </span>
          </span>) : ordered ? (<Ctext size={16} weight={FontWeight.Medium} color={ColorType.Accent} opacity={0.9}>
            {marker ?? `${index}.`}
          </Ctext>) : (<div style={{
                width: '0.6rem',
                height: '0.6rem',
                borderRadius: '50%',
                backgroundColor: 'rgb(102 254 193 / 80%)',
                boxShadow: 'rgb(0, 0, 0) 0px 1px 4px 0px, rgb(255 255 255 / 48%) 1px 1px 0px inset',
                marginTop: '0.5rem',
            }}/>)}
      </div>

      {/* Content */}
      <div style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
        }}>
        {children}
      </div>
    </div>
    );
}
